import React from "react";
import { ThemeProvider as MuiThemeProvider, createTheme } from "@mui/material/styles";
import { ThemeProvider } from "styled-components";

import theme from "./theme";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import Home from "pages/Home/Home";
import About from "pages/About";
import Help from "pages/Help";
import { AuthProvider } from "context/authContext";
import { UIProvider} from "context/ui/uiContext";
import DealerRoutes from "routes/DealerRoutes";
import CustomerRoutes from "routes/CustomerRoutes";
import Container from "components/layout/Container";
import UserAlert from "components/common/Alert/";
import LoadingSpinnerOverlay from "components/common/LoadingSpinnerOverlay";
/**
 * The main application component that sets up the global context providers, theming, and routing structure.
 *
 * This component serves as the root of the application. It wraps the entire app in multiple context providers:
 * - `MuiThemeProvider` from Material-UI (MUI) for MUI theming,
 * - `ThemeProvider` for styled-components theming, ensuring that the same theme can be used across both MUI components and styled-components,
 * - `AuthProvider` for managing authentication state across the application.
 *
 * Inside the providers, `Router` is used to define the application's navigation structure with `Routes` specifying
 * the paths to various components in the app. This setup allows for a centralized routing configuration, making it easier
 * to manage navigation and the application's layout structure. The `Container` component likely serves as a stylistic or layout wrapper
 * for the routes, providing consistent spacing or styling.
 * @returns {React.ReactElement} The root JSX element that includes global context providers for theming and authentication,
 * wrapped around the application's routing system.
 */
function App() {
  return (
    <MuiThemeProvider theme={theme}>
      <ThemeProvider theme={theme}>
        <UIProvider>
          <AuthProvider>
            <Router>
              <Container>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/dealer/*" element={<DealerRoutes />} />
                  <Route path="/about" element={About} />
                  <Route path="/help" element={Help} />
                  <Route path="/customer/*" element={<CustomerRoutes />} />
                </Routes>
              </Container>
            </Router>
          </AuthProvider>
          <UserAlert />
          <LoadingSpinnerOverlay />
        </UIProvider>
      </ThemeProvider>
    </MuiThemeProvider>
  );
}

export default App;
